// Angular
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';

// Third Party
import { finalize, tap } from 'rxjs';
import Papa from 'papaparse';

// Services
import { CustomerService } from '@services/customer.service';

// Models
import { Customer, INTEGRATIONS_LIST } from './../defs';

// Constants
import { Action, Active } from '@constants/enum';
import { CustomPaginator } from '@utils/custom-paginator';

// Components
import { LoadingSpinnerComponent } from '@components/common-components';

enum CustomerStatus {
  Archived = 0,
  Billable = 1,
  Demo = 2,
}

@Component({
  selector: 'app-customer-table',
  imports: [
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
    MatSidenavModule,
    RouterLink,
    NgClass,
    LoadingSpinnerComponent,
  ],
  templateUrl: './customer-table.component.html',
  styleUrl: './customer-table.component.scss',
  providers: [{ provide: MatPaginatorIntl, useValue: CustomPaginator() }],
})
export class CustomerTableComponent implements OnInit {
  displayedColumns = [
    'customerName',
    'tenantkey',
    'stack',
    'applicationAccess',
    'integrationsAccess',
    'idp',
    'status',
    'edit',
  ];

  readonly defaultData = signal<Customer[]>([]);

  readonly search = signal('');
  readonly status = signal<string[]>([]);
  readonly sort = signal<Sort>({ active: 'customerName', direction: 'asc' });

  readonly statusList = Object.keys(CustomerStatus).filter((key) => isNaN(Number(key)));

  readonly integrationOptions = Object.values(INTEGRATIONS_LIST);

  readonly pageSize = signal(50);
  readonly pageIndex = signal(0);

  readonly loading = signal(true);
  readonly error = signal(false);

  readonly filteredData = computed(() => {
    let data = this.defaultData();

    // Filter on status
    if (this.status().length > 0) {
      const selectedValues = this.status().map(
        (name) => CustomerStatus[name as keyof typeof CustomerStatus],
      );
      data = data.filter((element) => selectedValues.includes(element.status));
    }

    // Search
    const searchTerm = this.search().toLowerCase();
    if (searchTerm) {
      data = data.filter(
        (element) =>
          element.name.toLowerCase().includes(searchTerm) ||
          element.tenantKey.toLowerCase().includes(searchTerm) ||
          element.stack.name.toLowerCase().includes(searchTerm),
      );
    }

    // Sort
    return this.sortData(this.sort(), data);
  });

  readonly resultsLength = computed(() => this.filteredData().length);

  readonly sortedData = computed(() => {
    const data = this.filteredData();
    return data.slice(this.pageIndex() * this.pageSize(), (this.pageIndex() + 1) * this.pageSize());
  });

  readonly action = Action;
  readonly active = Active;

  readonly CustomerStatus = CustomerStatus;

  public addDialog = inject(MatDialog);
  private customerService = inject(CustomerService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.getTenant();
  }

  getTenant() {
    this.loading.set(true);
    this.customerService
      .getTenantList()
      .pipe(
        tap((data) => this.defaultData.set(data)),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => this.error.set(true),
      });
  }

  selectSort(sort: Sort) {
    this.sort.set(sort);
  }

  sortData(sort: Sort, dataToBeSorted: Customer[]) {
    const data = dataToBeSorted.slice();
    if (!sort.active || sort.direction === '') {
      return data;
    }
    return data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'customerName':
          return this.compare(a.name.toLowerCase(), b.name.toLowerCase(), isAsc);
        case 'tenantkey':
          return this.compare(a.tenantKey.toLowerCase(), b.tenantKey.toLowerCase(), isAsc);
        case 'stack':
          return this.compare(a.stack.name, b.stack.name, isAsc);
        case 'status':
          return this.compare(a.status, b.status, isAsc);
        default:
          return 0;
      }
    });
  }

  compare(
    a: number | string | Date | boolean,
    b: number | string | Date | boolean,
    isAsc: boolean,
  ) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  statusSelect(status: string[]) {
    this.status.set(status);
    this.pageIndex.set(0);
  }

  searchTable(search: string) {
    this.search.set(search);
    this.pageIndex.set(0);
  }

  handlePageEvent(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.pageIndex.set(event.pageIndex);
  }

  exportData() {
    const now = new Date();
    const fileName = `customers_${now.getDate()}_${now.getMonth() + 1}_${now.getFullYear()}.csv`;
    const blob = new Blob([this.ConvertToCSV(this.sortedData())], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  }

  ConvertToCSV(customers: Customer[]): string {
    const ignored = [
      'address',
      'emailPassword',
      'phone',
      'profileModules',
      'profileType',
      'emailVerified',
    ];

    const fields = Object.keys(customers[0]).filter((key) => !ignored.includes(key));

    const transformed = customers.map((customer) => {
      const row: Record<string, string> = {};
      for (const field of fields) {
        let value = customer[field];
        if (field === 'status') {
          value = CustomerStatus[Number(value)];
        } else if (field === 'stack') {
          value = customer.stack?.name ?? '';
        }
        row[field] = value;
      }
      return row;
    });

    return Papa.unparse(transformed, { columns: fields });
  }

  getIntegrations(element: Customer): string {
    const options = this.integrationOptions.filter((integration) => element[integration.property]);
    if (options.length === 0) return '-';

    const grouped = options.reduce(
      (acc, integration) => {
        const type = integration.type;
        acc[type.name] = (acc[type.name] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return Object.entries(grouped)
      .map(([type, count]) => `${type} (${count})`)
      .join(', ');
  }

  getIntegrationsHover(element: Customer): string[] {
    return this.integrationOptions
      .filter((integration) => element[integration.property])
      .map((integration) => integration.label);
  }

  getApplications(element: Customer): string {
    const apps = [];
    if (element.assetIntelligenceAccess) apps.push('AI');
    if (element.fleetIntelligenceAccess) apps.push('FI');
    if (element.safetyIntelligenceAccess) apps.push('SI');
    if (element.businessIntelligenceAccess) apps.push('BI');
    return apps.join(', ');
  }
}
